import { observer } from "mobx-react-lite"
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react"
import {
  TextInput,
  NativeSyntheticEvent,
  TextInputSelectionChangeEventData,
  Modal,
  DeviceEventEmitter,
  Keyboard,
} from "~/ui/primitives"
import styled from "~/ui/styled"
import useNavigation from "~hooks/useNavigation"
import { useModal } from "~hooks/useModal"
import { useConstant } from "~hooks/use-constant"
import useGroupActions from "~hooks/useGroupActions"
import { REFRESH_CHANNEL_LIST_EVENT } from "~pages/app-stack/channel-list/constants"
import { Message } from "../../models"
import { useStores } from "~models"
import { useActiveChat } from "~hooks/useActiveChat"
import { ChatTypes, MessageTypes } from "../../proto"
import { debounceLeading, globalToast } from "../../utils"
import { basename } from "../../utils/path"
import { ViewMode } from "../chat/view-mode"
import DefaultMode from "./default-mode"
import PhotosMode from "./photos-mode"
import EmojiMode from "./emoji-mode"
import MentionMode from "./mention-mode"
import VoiceRecordMode from "./voice-record-mode"
import ChannelMode from "./channel-mode"
import { InputUI } from "./input-ui"
import { ReplyBar } from "../reply-bar"
import H5MediaInput, { type H5MediaInputRef } from "./h5-media-input"
import { MaskLoading } from "~components/mask-loading"

function fileToAttachment(file: File) {
  const uri = URL.createObjectURL(file)
  const isVideo = file.type.startsWith("video/")
  return {
    id: `${file.name}-${file.lastModified}`,
    uri,
    previewUri: uri,
    path: uri,
    mediaType: isVideo ? "video" : "photo",
    filename: file.name,
    size: file.size,
    type: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
    content_type: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
  }
}

const Wrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
  height: auto;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

interface Props {
  viewMode: ViewMode
  listRef?: React.RefObject<any>
  setViewMode?: (val) => void
  isReplyBarVisible: boolean
  setIsReplyBarVisible: (val: boolean) => void
  popupOptionListModal: { visible: boolean; message: Message }
}

const MessageInput: React.FC<Props> = ({
  viewMode,
  listRef,
  setViewMode,
  isReplyBarVisible,
  setIsReplyBarVisible,
  popupOptionListModal,
}) => {
  // chat state
  const navigation = useNavigation()
  const { chatStore, messageStore, userStore, apiStore } = useStores()
  const { getChannelChatInfo, handleMemberKickedOutGroup } = useGroupActions()
  const { chat } = useActiveChat()
  // const data = chat?.messages.slice() || []

  const inputRef = React.useRef<TextInput>(null)
  const mediaInputRef = React.useRef<H5MediaInputRef>(null)
  const { Dialog } = useModal()
  // 消息内容
  const [content, setContent] = useState<string>(chat?.draft || "")
  const [selection, setSelection] = useState<TextInputSelectionChangeEventData["selection"]>({
    start: 0,
    end: content.length,
  })
  // 输入UI状态
  const [mode, setMode] = useState<InputUI>(InputUI.Default)
  // 多選照片
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [selectedPhotos, setSelectedPhotos] = useState([])
  const [channelChatInfo, setChannelChatInfo] = useState(null)

  const [isUploadVideoCoverting, setIsUploadVideoCoverting] = useState(false)

  const onSelectionChange = useCallback((e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
    setSelection(e.nativeEvent.selection)
  }, [])

  const getChannelGroupSettingInfo = useCallback(async () => {
    try {
      if (!chat) return
      if (chat.type === ChatTypes.PRIVATE) return
      if (!chat?.id) return
      const res = await apiStore.getChannelGroupSettingInfo({ chat_id: chat.id, type: chat.type === ChatTypes.CHANNEL ? 2 : 1 })
      const pinnedMessage = res?.data?.pinned_message || null
      if ([ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(chat.type)) {
        const allMute = !!res?.data?.all_mute
        chat?.setAllMute?.(allMute)
        if (typeof res?.data?.allow_history_msg === "boolean") {
          chat?.setAllowHistoryMsg?.(res.data.allow_history_msg)
        }
        setChannelChatInfo(res?.data)
      }
      if (chat.type === ChatTypes.CHANNEL) {
        setChannelChatInfo(res?.data)
      }
      if (pinnedMessage) {
        chat.setPinnedMessage(pinnedMessage?.message_id, pinnedMessage?.content)
      }
    } catch {
      // ignore
    }
  }, [apiStore, chat?.id, chat?.type])

  // 进入聊天室时拉取群设置（用于全群禁言等），避免必须先进入群管理页才生效
  useEffect(() => {
    getChannelGroupSettingInfo()
  }, [chat?.chat_id])

  const ensureCanSpeak = () => {
    if ([ChatTypes.GROUP, ChatTypes.GROUP_MINE, ChatTypes.GROUP_DRAGON].includes(chat.type) && !chat.isCurrentUserCanSpeak) {
      globalToast.show("全群禁言中")
      return false
    }
    return true
  }

  /** 与 chat-screen 一致：inverted 列表 newest 在 index 0 */
  const scrollListToEnd = useCallback(() => {
    if (!listRef?.current || !chat) return
    if (chat.sortedMessages.length === 0) return
    requestAnimationFrame(() => {
      try {
        listRef.current.scrollToIndex({ index: 0, animated: true })
      } catch {
        requestAnimationFrame(() => {
          try {
            listRef.current?.scrollToIndex({ index: 0, animated: true })
          } catch {
            /* list 尚未完成布局 */
          }
        })
      }
    })
  }, [listRef, chat])

  // 发送消息
  const sendMessage = () => {
    if (!ensureCanSpeak()) return
    const text = content.trim()
    if (text === "") {
      globalToast.show("消息不能为空")
      return
    }

    if (chat.type !== ChatTypes.GROUP) {
      // const receiverContact = contactStore.byUserId(chat.receiver.id)
      // receiverContact?.deleted &&
      //   chat.newMessage({
      //     content: "",
      //     type: MessageTypes.TEXT,
      //     status: MessageStatus.READ,
      //     notify_type: NotifyType.CONTACT_DELETED,
      //   })
    }
    const re = /@[\u4e00-\u9fa5\w]+/g
    const mentionIds: Array<number> = text.match(re)?.reduce((acc: number[], i: string) => {
      const username = i.replace("@", "")
      const user = userStore.getUserByUsername(username)
      if (user) acc.push(user.id)
      return acc
    }, [])

    // 組成訊息
    const message = chat.newMessage({
      content: text,
      type: MessageTypes.TEXT,
      mention_ids: mentionIds || [],
      reply_msg_id: isReplyBarVisible ? popupOptionListModal.message?.message_id || null : null,
    })

    // 送出訊息
    messageStore.sendMessageByMessageType(message)
    scrollListToEnd()

    // 送出訊息後清除輸入框資料以及清除回覆Bar
    if (message.type === MessageTypes.TEXT) {
      // hack way to prevent linebreak when hit enter key
      setTimeout(() => {
        setContent("")
        setIsReplyBarVisible(false)
        chat.clearDraft()
      }, 3)
    }
  }
  const onVoiceRecorded = useCallback(async ({ path, duration }) => {
    if (!ensureCanSpeak()) return
    const message = chat.newMessage({
      content: "",
      type: MessageTypes.VOICE,
      attach_path: path,
      attach: {
        filename: basename(path),
        length: duration,
        type: "audio/webm",
      },
      reply_msg_id: isReplyBarVisible ? popupOptionListModal.message?.message_id || null : null,
    })
    setIsReplyBarVisible(false)
    messageStore.sendMessageByMessageType(message)
    scrollListToEnd()
  }, [scrollListToEnd])
  const submitSelectedPhoto = useCallback((photo) => {
    if (!ensureCanSpeak()) return
    const { uri, ...other } = photo
    const message = chat.newMessage({
      content: "",
      attach_path: uri,
      attach: {
        ...other,
      },
      type: MessageTypes.IMAGE,
      reply_msg_id: isReplyBarVisible ? popupOptionListModal.message?.message_id || null : null,
    })
    setIsReplyBarVisible(false)
    setViewMode(ViewMode.ScrollView)
    messageStore.sendMessageByMessageType(message)
    scrollListToEnd()
  }, [scrollListToEnd, setViewMode])
  const submitSelectedPhotos = useCallback(async (photos) => {
    if (!ensureCanSpeak()) return
    setSelectedPhotos([])
    setIsReplyBarVisible(false)
    setViewMode(ViewMode.ScrollView)
    const datas = [[], []]
    photos.forEach((photo) => {
      if (photo.mediaType === "photo") {
        datas[0].push(photo)
      } else if (photo.mediaType === "video") {
        datas[1].push(photo)
      }
    })
    photos = datas[0]
    if (photos.length) {
      const message = chat.newMessage({
        content: "",
        attach: {
          imgs: photos,
        },
        type: MessageTypes.IMAGES,
        reply_msg_id: isReplyBarVisible ? popupOptionListModal.message?.message_id || null : null,
      })
      messageStore.sendMessageByMessageType(message)
    }
    const videos = datas[1]
    if (videos.length) {
      const covertedVideos = await Promise.all(
        videos.map(async (video) => ({
          type: video.type || "video/mp4",
          filename: video.filename,
          uri: video.uri,
        })),
      )
      covertedVideos.forEach((coverted, i) => {
        const { uri, type, filename } = coverted
        const video = videos[i]
        const message = chat.newMessage({
          content: "",
          attach_path: uri,
          attach: {
            ...video,
            type,
            filename,
            uri,
          },
          type: MessageTypes.VIDEO,
          reply_msg_id: isReplyBarVisible ? popupOptionListModal.message?.message_id || null : null,
        })
        messageStore.sendMessageByMessageType(message)
      })
    }
    scrollListToEnd()
  }, [scrollListToEnd])

  const onTypingStarted = useConstant(() =>
    debounceLeading(() => {
      chatStore.onTypingStarted()
    }),
  )

  const onKeyPress = (key: string) => {
    // if (key === "@") {
    //   setMode(InputUI.Mention)
    // }
  }
  
  const onChangeText = (value: string) => {
    setContent(value)
    chat.setDraft(value)
    onTypingStarted()
  }

  const addNewContentToInput = (value: string) => {
    onChangeText(
      content.slice(0, selection.end) + value + content.slice(selection.end, content.length),
    )
  }

  // 相册选择
  const onPhotos = useCallback(() => {
    setViewMode?.(ViewMode.Default)
    setMode((current) => (current === InputUI.Photos ? InputUI.Default : InputUI.Photos))
  }, [setViewMode])

  const handleCameraCapture = useCallback(
    (file: File) => {
      if (!ensureCanSpeak()) return
      submitSelectedPhoto(fileToAttachment(file))
    },
    [submitSelectedPhoto],
  )

  const handleGallerySelect = useCallback(
    (files: FileList) => {
      if (!ensureCanSpeak()) return
      const picked = Array.from(files).map((file, index) => ({
        ...fileToAttachment(file),
        id: `${file.name}-${file.lastModified}-${index}`,
      }))
      setSelectedPhotos((prev) => {
        const merged = [...prev, ...picked]
        return merged.length > 9 ? merged.slice(0, 9) : merged
      })
      setMode(InputUI.Photos)
    },
    [],
  )

  // 开启相机（H5：调起系统相机/拍照）
  const onCameraOpen = useCallback(() => {
    if (!ensureCanSpeak()) return
    mediaInputRef.current?.openCamera()
  }, [])

  // 打開表情符號
  const onEmoji = useCallback(() => {
    setViewMode?.(ViewMode.Default)
    setMode((current) => (current === InputUI.Emoji ? InputUI.Default : InputUI.Emoji))
  }, [setViewMode])

  const onFile = useCallback(() => {
    setMode(InputUI.Files)
  }, [])

  const onVoice = useCallback(() => {
    setViewMode?.(ViewMode.Default)
    setMode((current) => {
      if (current === InputUI.Voice) {
        return InputUI.Typing
      }
      Keyboard.dismiss()
      return InputUI.Voice
    })
  }, [setViewMode])

  const onTransfer = useCallback(() => {
    navigation.navigate("Transfer", { userID: chat.receiver.id })
  }, [])

  const onFocus = useCallback(() => {
    setMode(InputUI.Typing)
  }, [])

  const onBlur = useCallback(() => {
    // 仅键盘输入态失焦时回到默认，避免打断表情/图片面板切换
    setMode((current) => (current === InputUI.Typing ? InputUI.Default : current))
  }, [])

  // 取消订阅频道
  const handleSubscribe = useCallback(() => {
    Dialog.show({
      title: "取消订阅",
      content: `确定要取消订阅 ${chat.title} 吗？`,
      onConfirm: async () => {
        try {
          const result = await handleMemberKickedOutGroup({ chat_id: chat.id, chat_cid: chat.chat_id })
          if (result) {
            chatStore.remove(chat)
            globalToast.show("取消订阅成功")
            DeviceEventEmitter.emit(REFRESH_CHANNEL_LIST_EVENT)
            navigation.goBack()
          }
        } catch (error) {
          globalToast.show(error?.message || "取消订阅失败")
        }
          
      },
    })
  }, [])

  useEffect(() => {
    if (mode === InputUI.Typing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [mode])

  useEffect(() => {
    if (mode !== InputUI.Photos) {
      setSelectedPhoto(null)
      setSelectedPhotos([])
    }
  }, [mode])

  useEffect(() => {
    if (chat?.chatTypeEnum === "CHANNEL") {
      // CHAT_SETTING_INFO 需要数字 chat_id（chat.id），传 cid/短码会被服务端判定“频道不存在”
      if (chat.id) getChannelChatInfo(chat.id, chat.chat_id)
      else getChannelChatInfo(chat.chat_id)
    }
  }, [chat?.chatTypeEnum, chat?.chat_id, getChannelChatInfo])

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener("RefreshChannelChatInfo", () => {
      getChannelGroupSettingInfo()
    })
    return () => sub.remove()
  }, [chat?.chat_id, getChannelGroupSettingInfo])

  const prevViewModeRef = useRef(viewMode)
  useLayoutEffect(() => {
    const prev = prevViewModeRef.current
    prevViewModeRef.current = viewMode

    // 仅在用户开始滚动消息列表时收起扩展面板，避免打开表情/图片时被误关闭
    if (viewMode === ViewMode.ScrollView && prev !== ViewMode.ScrollView) {
      setMode((current) => {
        const panelModes = [InputUI.Photos, InputUI.Emoji, InputUI.Mention, InputUI.Files, InputUI.Typing]
        return panelModes.includes(current) ? InputUI.Default : current
      })
    }
  }, [viewMode])

  const showDefaultInput =
    chat &&
    (["PRIVATE", "GROUP"].includes(chat.chatTypeEnum) ||
      (chat.chatTypeEnum === "CHANNEL" && chat.isCurrentUserIsAdmin))

  const panelContent = (
    <>
      {mode === InputUI.Photos && (
        <PhotosMode
          selectedPhoto={selectedPhoto}
          setSelectedPhoto={setSelectedPhoto}
          selectedPhotos={selectedPhotos}
          setSelectedPhotos={setSelectedPhotos}
          submitSelectedPhoto={submitSelectedPhoto}
          submitSelectedPhotos={submitSelectedPhotos}
        />
      )}
      {mode === InputUI.Emoji && <EmojiMode onChangeText={addNewContentToInput} />}
      {mode === InputUI.Mention && !!chat?.usersByName?.length && (
        <MentionMode onChangeText={addNewContentToInput} onClose={onFocus} />
      )}
      {mode === InputUI.Voice && <VoiceRecordMode onVoiceRecorded={onVoiceRecorded} />}
    </>
  )

  if (!chat) return null

  return (
    <Wrapper>
      <H5MediaInput
        ref={mediaInputRef}
        onCameraCapture={handleCameraCapture}
        onGallerySelect={handleGallerySelect}
      />
      <Modal animationType="fade" transparent visible={isUploadVideoCoverting}>
        <MaskLoading text="影片转码中..." color="white" />
      </Modal>
      {isReplyBarVisible && popupOptionListModal.message && (
        <ReplyBar message={popupOptionListModal.message} setIsReplyBarVisible={setIsReplyBarVisible} />
      )}
      {showDefaultInput && (
        <DefaultMode
          mode={mode}
          inputRef={inputRef}
          chat={chat}
          content={content}
          channelChatInfo={channelChatInfo ?? chat.channelChatInfo}
          onVoice={onVoice}
          onPhotos={onPhotos}
          onCameraOpen={onCameraOpen}
          onEmoji={onEmoji}
          onFile={onFile}
          onTransfer={onTransfer}
          onKeyPress={onKeyPress}
          onSelectionChange={onSelectionChange}
          onChangeText={onChangeText}
          onFocus={onFocus}
          onBlur={onBlur}
          onSubmit={sendMessage}
          selectedPhotos={selectedPhotos}
          submitSelectedPhotos={submitSelectedPhotos}
          setMode={setMode}
        >
          {panelContent}
        </DefaultMode>
      )}
      {chat.chatTypeEnum === "CHANNEL" && (
        <ChannelMode
          channelChatInfo={channelChatInfo ?? chat.channelChatInfo}
          isCurrentUserIsAdmin={chat.isCurrentUserIsAdmin}
          onSubscribe={handleSubscribe}
        />
      )}
    </Wrapper>
  )
}
export default observer(MessageInput)
